import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classes from 'classnames';
import UseScreenSize from '@/hooks/UseScreenSize';
import { Outlet, useNavigate } from 'react-router-dom';
import useScrollToTop from '@/hooks/UseScrollToTop';
import UserUtils from '@/utils/user';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import { View } from '@/types/common';
import { ROUTE_NAMES } from '@/router';
import { useDrawer } from '@/context/DrawerContext';
import { t } from 'i18next';
import SVG from 'react-inlinesvg';
import commonUtils from '@/utils/common';
import clsx from 'clsx';

import PageWrap from '@/components/PageWrap';
import TheSidebar from '@/components/TheSidebar/TheSidebar';
import styles from '@/components/microphone-permissions/MicrophonePermissionsInfo.module.scss';
import mainViewStyles from './MainView.module.scss';

import classesIcon from '@/assets/svg/classes.svg';
import classroomIcon from '@/assets/svg/classroom.svg';
import dashboardIcon from '@/assets/svg/dashboard.svg';
import homepageIcon from '@/assets/svg/homepage.svg';
import tutorIcon from '@/assets/svg/tutor.svg';
import profileIcon from '@/assets/svg/my-profile.svg';
import Navbar from '@/components/Navbar/Navbar';
import MicrophonePermissionsInfo, {
  MicrophoneErrorTypes,
} from '@/components/microphone-permissions/MicrophonePermissionsInfo';
import Footer from '@/components/Footer/Footer';
import AppIcon from '@/components/GenericTable/Icon/Icon';
import UserDropdown from '@/components/school-management/UserDropdown/UserDropdown';
import NotificationsBadge from '@/components/Notifications/NotificationsBadge/NotificationsBadge';
import SettingsModal from '@/admin/components/SettingsModal/SettingsModal';

const MainView = () => {
  const { isMobile } = UseScreenSize();
  const navigate = useNavigate();
  const { user } = useAppSelector(authSelector);
  const drawer = useDrawer();
  const isTutorActive = user?.metadata?.organizationDetails?.tutorData?.isTutorActive || false;
  const isTeacher = UserUtils.isTeacher(user);
  const mainContentRef = useRef<HTMLDivElement>(null);
  const avatarButtonRef = useRef<HTMLButtonElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  useScrollToTop(isTeacher && !isMobile ? mainContentRef : undefined);

  const [showMicrophoneInfo, setShowMicrophoneInfo] = useState<MicrophoneErrorTypes | null>(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const openSettings = useCallback(() => setIsSettingsOpen(true), []);
  const closeSettings = useCallback(() => setIsSettingsOpen(false), []);

  const firstName = user?.metadata?.firstName ?? '';
  const lastName = user?.metadata?.lastName ?? '';
  const avatarName = user?.metadata?.userSettings?.avatar?.avatarName;
  const avatarColor = (user?.metadata?.userSettings?.avatar as any)?.avatarColor ?? '';
  const avatarImg = avatarName ? commonUtils.getAvatarImage(avatarName) : null;
  const avatarInitials = [firstName, lastName]
    .filter(Boolean)
    .map(name => name[0])
    .join('');

  const teacherViewTabs = useMemo(() => {
    const tabs = [
      {
        icon: classesIcon,
        name: ROUTE_NAMES.MY_CLASSES,
        path: '/dashboard/my-classes/',
        text: 'settings.my_classes',
        order: 1,
      },
      {
        icon: classroomIcon,
        name: ROUTE_NAMES.MY_STUDENTS,
        path: '/dashboard/my-students',
        text: 'settings.my_students',
        order: 2,
      },
      {
        icon: dashboardIcon,
        name: ROUTE_NAMES.GAMES,
        path: '/dashboard/games',
        text: 'settings.my_games',
        order: 3,
      },
    ];

    if (isTutorActive) {
      tabs.push({
        icon: tutorIcon,
        name: ROUTE_NAMES.MY_TUTOR,
        path: '/dashboard/my-tutor',
        text: 'settings.my_tutor',
        order: 3,
      });
    }

    return tabs;
  }, [user]);

  const studentViewTabs = useMemo(() => {
    const tabs = [
      {
        icon: homepageIcon,
        name: ROUTE_NAMES.GAMES,
        path: '/dashboard/games',
        text: 'homepage',
        order: 1,
      },
      {
        icon: profileIcon,
        name: ROUTE_NAMES.PROFILE,
        path: '/dashboard/profile',
        text: 'myProfile',
        order: 2,
      },
    ];

    if (isTutorActive) {
      tabs.push({
        icon: tutorIcon,
        name: ROUTE_NAMES.MY_TUTOR,
        path: '/dashboard/my-tutor',
        text: 'settings.my_tutor',
        order: 3,
      });
    }

    return tabs;
  }, [user]);

  const viewTabs = useMemo<View[]>(() => {
    return isTeacher ? [...teacherViewTabs] : [...studentViewTabs];
  }, []);

  const onSelectView = (view: View) => {
    const teacherLevel = localStorage.getItem('teacher-level');
    if (
      view.name == ROUTE_NAMES.GAMES &&
      isTeacher &&
      !user?.progress?.userLevel &&
      (teacherLevel === null || teacherLevel === '')
    ) {
      return navigate('/dashboard/level-test');
    }
    navigate(view.path);
  };

  useEffect(() => {
    let isActive = true;

    const checkMicrophonePermission = async () => {
      const hasPermission = localStorage.getItem('microphonePermission');
      if (
        (user?.metadata.role === 'teacher' || user?.metadata.role === 'student') &&
        hasPermission === null
      ) {
        const { isAllowed, errorType } = await commonUtils.checkMicrophoneAccess();
        if (isActive) {
          switch (errorType || '') {
            case 'permission-denied':
            case 'permission-prompt':
            case 'NotAllowedError':
              setShowMicrophoneInfo(MicrophoneErrorTypes.permissions);
              localStorage.setItem('microphonePermission', 'false');
              break;
            case 'NotFoundError':
            case 'OverconstrainedError':
            case 'TypeError':
              setShowMicrophoneInfo(MicrophoneErrorTypes.device_not_found);
              localStorage.setItem('microphonePermission', 'false');
              break;
            default:
              setShowMicrophoneInfo(null);
              localStorage.setItem('microphonePermission', 'true');
              break;
          }
        }
      }
    };

    checkMicrophonePermission();

    return () => {
      isActive = false;
    };
  }, []);

  const isTeacherView = isTeacher && !isMobile;
  const isStudentDesktopView = !isTeacher && !isMobile;

  return (
    <>
      <div
        className={`flex ${isTeacherView ? 'h-screen overflow-hidden min-h-full' : 'min-h-full'}`}
        style={{
          pointerEvents: showMicrophoneInfo ? 'none' : 'auto',
        }}
      >
        {isMobile ? (
          <Navbar />
        ) : (
          <TheSidebar
            viewTabs={viewTabs}
            emitSelectView={onSelectView}
            isCollapsed={isSidebarCollapsed}
            onCollapsedChange={setIsSidebarCollapsed}
          />
        )}

        {showMicrophoneInfo && (
          <div className={styles.permissionOverlay}>
            <div className={styles.permissionContent}>
              <MicrophonePermissionsInfo
                onClose={() => setShowMicrophoneInfo(null)}
                errorType={showMicrophoneInfo}
              />
            </div>
          </div>
        )}

        <div
          className={classes({
            [mainViewStyles.teacherView]: isTeacherView,
            [mainViewStyles.studentDesktopView]: isStudentDesktopView,
            [mainViewStyles.view]: isMobile,
          })}
        >
          {isTeacherView && (
            <>
              <div className={mainViewStyles.mainViewHeader}>
                <button
                  className={clsx(mainViewStyles.collapseButton, {
                    [mainViewStyles.collapsed]: isSidebarCollapsed,
                  })}
                  onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                  aria-label={
                    isSidebarCollapsed ? t('expandSidebar') || '' : t('collapseSidebar') || ''
                  }
                >
                  <AppIcon
                    icon='leftArrow'
                    className={clsx(mainViewStyles.collapseIcon, {
                      [mainViewStyles.collapsed]: isSidebarCollapsed,
                    })}
                  />
                </button>

                <div className={mainViewStyles.headerRight}>
                  <NotificationsBadge />

                  <button
                    ref={avatarButtonRef}
                    className={mainViewStyles.avatarButton}
                    onClick={() => setIsDropdownOpen(prev => !prev)}
                    aria-label='Open user menu'
                    aria-haspopup='true'
                    aria-expanded={isDropdownOpen}
                  >
                    <div
                      className={mainViewStyles.avatarCircle}
                      style={{ backgroundColor: avatarColor || undefined }}
                    >
                      {avatarImg ? (
                        <SVG src={avatarImg} className={mainViewStyles.avatarSvg} />
                      ) : (
                        <span className={mainViewStyles.avatarInitials}>{avatarInitials}</span>
                      )}
                    </div>
                  </button>

                  <div className={mainViewStyles.dropdownWrapper}>
                    <UserDropdown
                      isOpen={isDropdownOpen}
                      onOpenSettings={drawer.openDrawer}
                      onContactUs={drawer.openContactUsDrawer}
                      onClose={() => setIsDropdownOpen(false)}
                      anchorRef={avatarButtonRef}
                    />
                  </div>
                </div>
              </div>

              <div ref={mainContentRef} className={mainViewStyles.teacherMainViewContent}>
                <PageWrap isTeacher={true}>
                  <Outlet />
                </PageWrap>

                <Footer className={mainViewStyles.teacherFooter} />
              </div>
            </>
          )}

          {isStudentDesktopView && (
            <>
              <div className={mainViewStyles.mainViewHeader}>
                <div />
                <div className={mainViewStyles.headerRight}>
                  <NotificationsBadge />

                  <button
                    ref={avatarButtonRef}
                    className={mainViewStyles.avatarButton}
                    onClick={() => setIsDropdownOpen(prev => !prev)}
                    aria-label='Open user menu'
                    aria-haspopup='true'
                    aria-expanded={isDropdownOpen}
                  >
                    <div
                      className={mainViewStyles.avatarCircle}
                      style={{ backgroundColor: avatarColor || '#3ad0f9' }}
                    >
                      {avatarImg ? (
                        <SVG src={avatarImg} className={mainViewStyles.avatarSvg} />
                      ) : (
                        <span className={mainViewStyles.avatarInitials}>{avatarInitials}</span>
                      )}
                    </div>
                  </button>

                  <div className={mainViewStyles.dropdownWrapper}>
                    <UserDropdown
                      isOpen={isDropdownOpen}
                      isStudent
                      onOpenSettings={openSettings}
                      onContactUs={drawer.openContactUsDrawer}
                      onClose={() => setIsDropdownOpen(false)}
                      anchorRef={avatarButtonRef}
                    />
                  </div>
                </div>
              </div>

              <PageWrap isTeacher={false}>
                <Outlet />
              </PageWrap>
            </>
          )}

          {isMobile && (
            <>
              <PageWrap isTeacher={false}>
                <Outlet />
              </PageWrap>
            </>
          )}
        </div>
      </div>
      {!isTeacherView && <Footer />}
      {!isTeacher && <SettingsModal show={isSettingsOpen} onClose={closeSettings} />}
    </>
  );
};

export default React.memo(MainView);
